import { z } from '@uturn/portal/zod';
import { addIssueCustom } from '../../../../zod/utils';
import { FIELD_KEYS, type FieldKey } from './field-keys';
import type { FieldOption } from './multi-update-types';
import { isComparable, isNoChange } from './multi-update-utils';

const priceSchema = z.preprocess(
	(v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
	z.coerce.number().finite().nonnegative(),
);

const locationId = z.preprocess(
	(v) => (v === '' || v == null ? undefined : v), // treat empty as missing
	z.union([
		z.coerce.number().int().positive('Must be > 0'), // numeric ids
		z.string().min(1, 'Required'), // string ids (e.g., Google)
	]),
);
const refSchema = z.string().trim();

const dateISOSchema = z.coerce.date().transform((d) => d.toISOString());

const carriersSchema = z.preprocess(
	(v) => (v == null || v === '' ? [] : v), // allow untouched/empty
	z.array(z.number().int().positive()),
);
const validatorRegistry: Partial<Record<FieldKey, z.ZodTypeAny>> = {
	[FIELD_KEYS.PRICE]: priceSchema,

	[FIELD_KEYS.DROP_DATE_FROM]: dateISOSchema,
	[FIELD_KEYS.DROP_DATE_UNTIL]: dateISOSchema,
	[FIELD_KEYS.DROP_REFERENCE]: refSchema,
	[FIELD_KEYS.DROP_LOCATION]: locationId,

	[FIELD_KEYS.PICKUP_DATE_FROM]: dateISOSchema,
	[FIELD_KEYS.PICKUP_DATE_UNTIL]: dateISOSchema,
	[FIELD_KEYS.PICKUP_REFERENCE]: refSchema,
	[FIELD_KEYS.PICKUP_LOCATION]: locationId,

	[FIELD_KEYS.INVOICE_REFERENCE]: refSchema,
	[FIELD_KEYS.ACCEPTANCE_PRICE]: z.coerce.boolean(),
	[FIELD_KEYS.CARRIERS]: carriersSchema,
	[FIELD_KEYS.PUBLISH_UNTIL]: dateISOSchema,
	[FIELD_KEYS.PUBLISH]: z.coerce.boolean(),
	[FIELD_KEYS.UNPUBLISH]: z.coerce.boolean(),
};
const fallbackSchema = z
	.string()
	.trim()
	.min(1, 'New value is required')
	.superRefine((v, ctx) => {
		if (!v) {
			addIssueCustom(ctx, 'formValidation.required');
		}
	});

export const createFormSchema = (fields: FieldOption[]) => {
	const optionMap = Object.fromEntries(
		fields.map((f) => [
			f.name,
			validatorRegistry[f.name as FieldKey] ?? fallbackSchema,
		]),
	);

	const typeMap: Record<string, FieldOption['inputType']> = Object.fromEntries(
		fields.map((f) => [f.name, f.inputType]),
	);

	const rowSchema = z
		.object({
			key: z.string(),
			currentValues: z.unknown().optional(),
			value: z.unknown(),
			locationSignature: z.string().optional(),
		})
		.superRefine((val, ctx) => {
			// 1) Validate value by the right schema
			const schema = optionMap[val.key] ?? fallbackSchema;
			const parsed = schema.safeParse(val.value);
			if (!parsed.success) {
				ctx.addIssue({
					code: 'custom',
					message: parsed.error.errors[0].message,
					path: ['value'],
				});
				return;
			}

			// 2) “No change” validation (only when comparable)
			const inputType = typeMap[val.key] ?? 'text';
			const baselineArr = Array.isArray(val.currentValues)
				? val.currentValues
				: [];

			if (!isComparable(baselineArr, inputType)) {
				return;
			}

			if (
				isNoChange(inputType, baselineArr, val.value, val.locationSignature)
			) {
				ctx.addIssue({
					code: 'custom',
					message: 'Nothing to update',
					path: ['value'],
				});
			}
		});

	return z.object({
		rows: z.array(rowSchema).min(1, 'At least one row is required'),
	});
};
